import { useCallback, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import type { QrCodeRecord, QrCustomization } from './types';

export interface UpdatePayload {
  version: number;
  campaign_name?: string;
  description?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  customization?: QrCustomization;
  logo_s3_key?: string | null;
  archived?: boolean;
}

export interface UpdateResult {
  data?: QrCodeRecord;
  error?: { code: string; message: string };
}

export function useUpdateQrCode(): {
  update: (id: string, payload: UpdatePayload) => Promise<UpdateResult>;
  loading: boolean;
} {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(false);

  const update = useCallback(
    async (id: string, payload: UpdatePayload): Promise<UpdateResult> => {
      if (!isAuthenticated || accounts.length === 0) {
        return { error: { code: 'not_authenticated', message: 'Sign in to edit QR codes' } };
      }
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        return { error: { code: 'config_missing', message: 'REACT_APP_API_URL is not set' } };
      }

      setLoading(true);
      try {
        const tokenResponse = { accessToken: await acquireApiToken(instance, accounts[0]) };
        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        };
        const res = await fetch(
          `${apiUrl}/admin/qr-codes/${encodeURIComponent(id)}`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify(payload),
          },
        );
        const json = await res.json();
        if (!res.ok) {
          return {
            error: {
              code: json.error || `http_${res.status}`,
              message: json.message || json.error || `HTTP ${res.status}`,
            },
          };
        }
        return { data: json.data as QrCodeRecord };
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to update QR code';
        return { error: { code: 'network_error', message: msg } };
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance, accounts],
  );

  return { update, loading };
}
